import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Loader2, ArrowLeft, Sun, Moon, CheckCircle2 } from '@/components/icons';
import { forgetPassword } from '../../lib/auth-client';
import { useTheme } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StackSketchLogo from '/StackSketch-FullLogo.svg';

export function ForgotPasswordPage() {
  const { isDark, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await forgetPassword(email);
      if (result.error) {
        setError(result.error.message || 'Failed to send reset email');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]">
      {/* Logo and branding */}
      <div className="mb-8 text-center">
        <img src={StackSketchLogo} alt="Stack Sketch" className="w-32 h-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        <p className="text-muted-foreground dark:text-muted-foreground">
          Reset your password
        </p>
      </div>

      {/* Auth Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border dark:border-border shadow-xl">
          {/* Header */}
          <CardHeader className="border-b border-border dark:border-border">
            <h3 className="text-lg font-bold text-foreground dark:text-foreground">
              Forgot Password?
            </h3>
            <CardDescription className="text-xs">
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          </CardHeader>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <CardContent className="p-6 space-y-4">
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Alert>
                      <AlertDescription>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold mb-1">Check your email</p>
                            <p>
                              If an account with that email exists, we've sent you a password reset link.
                              Please check your inbox and follow the instructions.
                            </p>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {!success && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      disabled={isLoading}
                      className="pl-10"
                    />
                  </div>
                </div>
              )}

              {!success && (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending reset link...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              )}

              {success && (
                <Link
                  to="/auth"
                  className="block w-full"
                >
                  <Button className="w-full" variant="default">
                    Back to Sign In
                  </Button>
                </Link>
              )}
            </CardContent>
          </form>

          {/* Footer */}
          {!success && (
            <CardFooter className="border-t border-border dark:border-border">
              <Link
                to="/auth"
                className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </CardFooter>
          )}
        </Card>
      </motion.div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed bottom-4 right-4 p-3 bg-card dark:bg-card border border-border dark:border-border rounded-full shadow-lg hover:bg-muted dark:hover:bg-muted transition-colors"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>
    </div>
  );
}

